import { useState, useEffect } from "react";
import BaseModal from "../BaseComponents/BaseModal/BaseModal";
import BoardCodeDropdown from "../Booking/BoardCodeDropdown";
import {
  Plane,
  Hotel,
  Bus,
  Shield,
  Package,
  Car,
  FileText,
  ChevronRight,
  ArrowLeft,
} from "lucide-react";
import styles from "./AddComponentModal.module.css";

const COMPONENT_TYPES = [
  {
    key: "Flight",
    label: "Flight",
    desc: "Outbound, return, or multi-leg segments",
    Icon: Plane,
    iconClass: "iconFlight",
  },
  {
    key: "Hotel",
    label: "Hotel / Accommodation",
    desc: "Hotel stay with check-in/out dates",
    Icon: Hotel,
    iconClass: "iconHotel",
  },
  {
    key: "Transfer",
    label: "Transfer",
    desc: "Ground transport between locations",
    Icon: Bus,
    iconClass: "iconTransfer",
  },
  {
    key: "Insurance",
    label: "Travel Insurance",
    desc: "Travel and cancellation insurance",
    Icon: Shield,
    iconClass: "iconInsurance",
  },
  {
    key: "Package",
    label: "Package Holiday",
    desc: "Pre-packaged offline holiday product",
    Icon: Package,
    iconClass: "iconPackage",
  },
  {
    key: "CarRental",
    label: "Car Rental",
    desc: "Rental vehicle for the travel period",
    Icon: Car,
    iconClass: "iconCarRental",
  },
  {
    key: "Visa",
    label: "Visa Service",
    desc: "Visa application and processing",
    Icon: FileText,
    iconClass: "iconVisa",
  },
];

const FIELD_DEFS = {
  Flight: [
    { key: "from", label: "From", placeholder: "e.g. Brussels (BRU)" },
    { key: "to", label: "To", placeholder: "e.g. Antalya (AYT)" },
    { key: "departure", label: "Departure", placeholder: "e.g. 14 Mar 2025 06:45", type: "datetime-local" },
    { key: "return", label: "Return", placeholder: "e.g. 21 Mar 2025 14:30", type: "datetime-local" },
    { key: "passengers", label: "Passengers", placeholder: "e.g. 2 Adults, 1 Child" },
    { key: "class", label: "Class", placeholder: "e.g. Economy", type: "select", options: ["Economy", "Premium Economy", "Business", "First"] },
    { key: "pnr", label: "PNR", placeholder: "e.g. TK-7X9K2M" },
    { key: "ticketNumber", label: "Ticket Number", placeholder: "e.g. 176-2345678900" },
    { key: "costPrice", label: "Cost Price (€)", placeholder: "0.00", type: "number" },
    { key: "sellingPrice", label: "Selling Price (€)", placeholder: "0.00", type: "number" },
  ],
  Hotel: [
    { key: "hotel", label: "Hotel Name", placeholder: "e.g. Rixos Premium Belek" },
    { key: "location", label: "Location", placeholder: "e.g. Belek, Turkey" },
    { key: "checkin", label: "Check-in", placeholder: "", type: "date" },
    { key: "checkout", label: "Check-out", placeholder: "", type: "date" },
    { key: "room", label: "Room Type & Board", placeholder: "e.g. Family Suite — All Inclusive" },
    { key: "nights", label: "Nights", placeholder: "e.g. 7 nights" },
    { key: "supplierRef", label: "Supplier Ref", placeholder: "e.g. HB-99234112" },
    { key: "costPrice", label: "Cost Price (€)", placeholder: "0.00", type: "number" },
    { key: "sellingPrice", label: "Selling Price (€)", placeholder: "0.00", type: "number" },
  ],
  Transfer: [
    { key: "transferType", label: "Transfer Type", placeholder: "e.g. Private Transfer", type: "select", options: ["Private Transfer", "Shared Transfer", "VIP Transfer"] },
    { key: "route", label: "Route (Outbound)", placeholder: "e.g. AYT → Rixos Belek" },
    { key: "returnRoute", label: "Route (Return)", placeholder: "e.g. Rixos Belek → AYT" },
    { key: "passengers", label: "Passengers", placeholder: "e.g. 3 pax" },
    { key: "date", label: "Transfer Date", placeholder: "", type: "date" },
    { key: "supplierRef", label: "Supplier Ref", placeholder: "e.g. TR-00193842" },
    { key: "costPrice", label: "Cost Price (€)", placeholder: "0.00", type: "number" },
    { key: "sellingPrice", label: "Selling Price (€)", placeholder: "0.00", type: "number" },
  ],
  Insurance: [
    { key: "policyType", label: "Policy Type", placeholder: "e.g. All-risk Travel Insurance" },
    { key: "coverage", label: "Coverage", placeholder: "e.g. 3 travelers" },
    { key: "validFrom", label: "Valid From", placeholder: "", type: "date" },
    { key: "validTo", label: "Valid To", placeholder: "", type: "date" },
    { key: "policyRef", label: "Policy Ref", placeholder: "e.g. INS-00123" },
    { key: "costPrice", label: "Cost Price (€)", placeholder: "0.00", type: "number" },
    { key: "sellingPrice", label: "Selling Price (€)", placeholder: "0.00", type: "number" },
  ],
  Package: [
    { key: "packageName", label: "Package Name", placeholder: "e.g. Turkey All Inclusive Family" },
    { key: "destination", label: "Destination", placeholder: "e.g. Antalya, Turkey" },
    { key: "departure", label: "Departure Date", placeholder: "", type: "date" },
    { key: "return", label: "Return Date", placeholder: "", type: "date" },
    { key: "passengers", label: "Passengers", placeholder: "e.g. 2 Adults, 1 Child" },
    { key: "boardType", label: "Board Type", placeholder: "e.g. All Inclusive", type: "boardCode" },
    { key: "supplierRef", label: "Supplier Ref", placeholder: "e.g. PKG-00456" },
    { key: "costPrice", label: "Cost Price (€)", placeholder: "0.00", type: "number" },
    { key: "sellingPrice", label: "Selling Price (€)", placeholder: "0.00", type: "number" },
  ],
  CarRental: [
    { key: "vehicleType", label: "Vehicle Type", placeholder: "e.g. Compact SUV" },
    { key: "pickupLocation", label: "Pick-up Location", placeholder: "e.g. Antalya Airport" },
    { key: "dropoffLocation", label: "Drop-off Location", placeholder: "e.g. Antalya Airport" },
    { key: "pickupDate", label: "Pick-up Date", placeholder: "", type: "date" },
    { key: "dropoffDate", label: "Drop-off Date", placeholder: "", type: "date" },
    { key: "driver", label: "Driver Name", placeholder: "e.g. Sarah Johnson" },
    { key: "supplierRef", label: "Supplier Ref", placeholder: "e.g. CR-00789" },
    { key: "costPrice", label: "Cost Price (€)", placeholder: "0.00", type: "number" },
    { key: "sellingPrice", label: "Selling Price (€)", placeholder: "0.00", type: "number" },
  ],
  Visa: [
    { key: "visaType", label: "Visa Type", placeholder: "e.g. Tourist Visa" },
    { key: "destinationCountry", label: "Destination Country", placeholder: "e.g. Turkey" },
    { key: "travelers", label: "Travelers", placeholder: "e.g. 3 travelers" },
    { key: "applicationDate", label: "Application Date", placeholder: "", type: "date" },
    { key: "visaStatus", label: "Status", placeholder: "", type: "select", options: ["Requested", "In Progress", "Completed", "Cancelled"] },
    { key: "supplierRef", label: "Supplier Ref", placeholder: "e.g. VS-00321" },
    { key: "costPrice", label: "Cost Price (€)", placeholder: "0.00", type: "number" },
    { key: "sellingPrice", label: "Selling Price (€)", placeholder: "0.00", type: "number" },
  ],
};

const STATUS_OPTIONS = ["Confirmed", "Pending", "Cancelled"];

const buildEmptyFields = (type) => {
  const defs = FIELD_DEFS[type] || [];
  const obj = {};
  defs.forEach((d) => {
    obj[d.key] = "";
  });
  return obj;
};

const fieldsToComponentFields = (type, formData) => {
  const defs = FIELD_DEFS[type] || [];
  return defs
    .filter((d) => d.key !== "costPrice")
    .map((d) => ({
      label: d.label,
      value: d.key === "sellingPrice" ? `€ ${Number(formData[d.key] || 0).toLocaleString("de-DE", { minimumFractionDigits: 2 })}` : formData[d.key] || "—",
    }));
};

const componentToFormData = (component) => {
  if (!component || !component.fields) return {};
  const data = {};
  component.fields.forEach((f) => {
    const defs = FIELD_DEFS[component.type] || [];
    const match = defs.find((d) => d.label === f.label);
    if (match) {
      let val = f.value;
      if (match.key === "sellingPrice" || match.key === "costPrice") {
        val = val.replace(/[€\s.]/g, "").replace(",", ".");
      }
      data[match.key] = val;
    }
  });
  if (component.costPrice !== undefined) data.costPrice = String(component.costPrice);
  return data;
};

const ICON_MAP_KEYS = {
  Flight: "flight",
  Hotel: "hotel",
  Transfer: "transfer",
  Insurance: "insurance",
  Package: "package",
  CarRental: "carrental",
  Visa: "visa",
};

const AddComponentModal = ({ open, onClose, mode = "add", initialData, onSave, preselectedType }) => {
  const [step, setStep] = useState(mode === "edit" ? "form" : "select");
  const [selectedType, setSelectedType] = useState(initialData?.type || "");
  const [formData, setFormData] = useState({});
  const [supplier, setSupplier] = useState("");
  const [status, setStatus] = useState("Pending");

  useEffect(() => {
    if (!open) return;
    if (mode === "edit" && initialData) {
      setSelectedType(initialData.type);
      setFormData(componentToFormData(initialData));
      setSupplier(initialData.supplier || "");
      setStatus(initialData.status || "Pending");
      setStep("form");
    } else if (preselectedType) {
      setSelectedType(preselectedType);
      setFormData(buildEmptyFields(preselectedType));
      setSupplier("");
      setStatus("Pending");
      setStep("form");
    } else {
      setStep("select");
      setSelectedType("");
      setFormData({});
      setSupplier("");
      setStatus("Pending");
    }
  }, [open, mode, initialData, preselectedType]);

  const handleSelectType = (typeKey) => {
    setSelectedType(typeKey);
    setFormData(buildEmptyFields(typeKey));
    setSupplier("");
    setStatus("Pending");
    setStep("form");
  };

  const handleBackToSelect = () => {
    setStep("select");
    setSelectedType("");
    setFormData({});
  };

  const handleFieldChange = (key, value) => {
    setFormData((prev) => ({ ...prev, [key]: value }));
  };

  const handleSubmit = () => {
    const fields = fieldsToComponentFields(selectedType, formData);
    const component = {
      id: initialData?.id || Date.now(),
      type: selectedType,
      iconKey: ICON_MAP_KEYS[selectedType] || "file",
      status,
      supplier: supplier || "Manual",
      fields,
      costPrice: parseFloat(formData.costPrice) || 0,
      sellingPrice: parseFloat(formData.sellingPrice) || 0,
    };
    onSave(component);
    onClose();
  };

  const typeConfig = COMPONENT_TYPES.find((t) => t.key === selectedType);
  const fieldDefs = FIELD_DEFS[selectedType] || [];

  const modalTitle =
    step === "select"
      ? "Add Component — Select Type"
      : mode === "edit"
      ? `Edit ${typeConfig?.label || "Component"}`
      : `Add ${typeConfig?.label || "Component"}`;

  return (
    <BaseModal
      open={open}
      onClose={onClose}
      title={modalTitle}
      width={step === "select" ? "640px" : "700px"}
      footer={
        step === "form" ? (
          <>
            <button className={styles.cancelBtn} onClick={onClose}>Cancel</button>
            <button className={styles.primaryBtn} onClick={handleSubmit}>
              {mode === "edit" ? "Save Changes" : "+ Add Component"}
            </button>
          </>
        ) : null
      }
    >
      {step === "select" && (
        <div className={styles.typeGrid}>
          {COMPONENT_TYPES.map((ct) => {
            const TIcon = ct.Icon;
            return (
              <div
                key={ct.key}
                className={styles.typeCard}
                onClick={() => handleSelectType(ct.key)}
              >
                <div className={`${styles.typeCardIcon} ${styles[ct.iconClass]}`}>
                  <TIcon size={18} />
                </div>
                <div className={styles.typeCardText}>
                  <div className={styles.typeCardLabel}>{ct.label}</div>
                  <div className={styles.typeCardDesc}>{ct.desc}</div>
                </div>
                <ChevronRight size={16} className={styles.typeCardArrow} />
              </div>
            );
          })}
        </div>
      )}

      {step === "form" && (
        <div className={styles.formWrapper}>
          {mode !== "edit" && (
            <button className={styles.backToSelect} onClick={handleBackToSelect}>
              <ArrowLeft size={13} /> Change component type
            </button>
          )}

          <div className={styles.typeIndicator}>
            {typeConfig && (
              <>
                <div className={`${styles.typeIndicatorIcon} ${styles[typeConfig.iconClass]}`}>
                  <typeConfig.Icon size={14} />
                </div>
                <span>{typeConfig.label}</span>
              </>
            )}
          </div>

          {/* ── Section: Component Info ── */}
          <div className={styles.formSection}>
            <div className={styles.formSectionHeader}>
              <span className={styles.formSectionTitle}>Component Info</span>
            </div>
            <div className={styles.formSectionBody}>
              <div className={styles.formGrid}>
                <div className={styles.field}>
                  <label>Supplier</label>
                  <input
                    placeholder="e.g. Hotelbeds, Airtuerk, Manual"
                    value={supplier}
                    onChange={(e) => setSupplier(e.target.value)}
                  />
                </div>
                <div className={styles.field}>
                  <label>Status</label>
                  <select value={status} onChange={(e) => setStatus(e.target.value)}>
                    {STATUS_OPTIONS.map((s) => (
                      <option key={s} value={s}>{s}</option>
                    ))}
                  </select>
                </div>
              </div>
            </div>
          </div>

          {/* ── Section: Product Details ── */}
          <div className={styles.formSection}>
            <div className={styles.formSectionHeader}>
              <span className={styles.formSectionTitle}>{typeConfig?.label || "Product"} Details</span>
            </div>
            <div className={styles.formSectionBody}>
              <div className={styles.formGrid}>
                {fieldDefs.filter((fd) => fd.key !== "costPrice" && fd.key !== "sellingPrice").map((fd) => (
                  <div key={fd.key} className={styles.field}>
                    <label>{fd.label}</label>
                    {fd.type === "boardCode" ? (
                      <BoardCodeDropdown
                        value={formData[fd.key] || ""}
                        onChange={(code) => handleFieldChange(fd.key, code)}
                        placeholder="Select board type…"
                      />
                    ) : fd.type === "select" ? (
                      <select
                        value={formData[fd.key] || ""}
                        onChange={(e) => handleFieldChange(fd.key, e.target.value)}
                      >
                        <option value="">— Select —</option>
                        {fd.options.map((opt) => (
                          <option key={opt} value={opt}>{opt}</option>
                        ))}
                      </select>
                    ) : (
                      <input
                        type={fd.type || "text"}
                        placeholder={fd.placeholder}
                        value={formData[fd.key] || ""}
                        onChange={(e) => handleFieldChange(fd.key, e.target.value)}
                      />
                    )}
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* ── Section: Pricing ── */}
          <div className={styles.formSection}>
            <div className={styles.formSectionHeader}>
              <span className={styles.formSectionTitle}>Pricing</span>
            </div>
            <div className={styles.formSectionBody}>
              <div className={styles.formGrid}>
                {fieldDefs.filter((fd) => fd.key === "costPrice" || fd.key === "sellingPrice").map((fd) => (
                  <div key={fd.key} className={styles.field}>
                    <label>{fd.label}</label>
                    <input
                      type="number"
                      placeholder={fd.placeholder}
                      value={formData[fd.key] || ""}
                      onChange={(e) => handleFieldChange(fd.key, e.target.value)}
                      step="0.01"
                    />
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}
    </BaseModal>
  );
};

export default AddComponentModal;